/* PICK A PICTURE SHE ALREADY HAS — OR UPLOAD ONE.
 *
 * FROM 2026-10-27 EVERY CLIENT GETS THIS. Most have no GHL (her direction,
 * 2026-10-26), so the first tab is her pictures KEPT IN BRILL
 * (`utils/brillPictures.js`, Supabase Storage, supabase/0047). Her GHL library
 * and Canva stay beside it, admin-only, until Connect GHL lands.
 *
 * The note below is from when GHL was the only home:
 *
 * Her logo, her headshot and anything else she has ever uploaded are sitting
 * in her CRM's media library. Brill does not need to host them — it needs to
 * know they are there and hand back a link. Her words, 2026-09-22: "mine is in
 * a logo folder in my files."
 *
 * So this is a picker, not an uploader, and every field it fills stays a URL.
 * The same shape as the Library picker: a button, a floating panel, one click
 * to choose. Read only when she opens it — a settings screen that merely
 * OFFERS a picture should not call anyone's API until she asks.
 */
import { useEffect, useRef, useState } from "react";
import { prepareBlob, prepareImage } from "../../utils/imageUpload";
import { listPictures, savePicture } from "../../utils/brillPictures";
import { useSupabase } from "../../supabaseClient";
import { useSession, useUser } from "@clerk/clerk-react";
import { isAdminEmail } from "../../utils/admin";
import { ChevronRight, Folder, FolderPlus, Image as ImageIcon, Upload as UploadIcon } from "lucide-react";
import { B, btnGhost, input } from "./tokens";
import { floatingPanel, useAnchoredPanel } from "./ui";
import { CanvaPane } from "./canvaPicker";

/**
 * CAN THIS PERSON PICK FROM A CRM AT ALL? (Not "can she pick a picture" — since
 * 2026-10-27 everyone can, from Brill. This gates the GHL and Canva tabs and
 * every GHL send.) Her rule, 2026-09-23: "not everyone
 * has GHL so if they don't it will require placeholder for now." Today the only
 * connection is her own token in the environment, and /api/ghl refuses
 * everyone else — so offering the button to a client is offering an error.
 * When the per-client Connect screen lands (deferred by her until client
 * security is settled), THIS is the one place that learns to read it.
 */
export function useCrmConnected() {
  const { user } = useUser();
  return isAdminEmail(user?.primaryEmailAddress?.emailAddress);
}

/* THE FOLDER SHE WAS LAST IN, per kind of picture (`remember`) — a story's
   picture opens where story pictures go. A per-browser convenience, so every
   read is guarded and a missing value just means the top. */
const TOP = [{ id: null, name: "All pictures" }];
const pathKey = (k) => `brill.media.folder.${k}`;
const readPath = (k) => {
  if (!k) return TOP;
  try {
    const v = JSON.parse(window.localStorage.getItem(pathKey(k)) || "null");
    return Array.isArray(v) && v.length && v[0].id === null ? v : TOP;
  } catch { return TOP; }
};
const writePath = (k, path) => { if (!k) return; try { window.localStorage.setItem(pathKey(k), JSON.stringify(path)); } catch { /* private window */ } };

/**
 * @param onPick   (url) => void — the caller writes it wherever it goes
 * @param label    the button's words
 * @param remember a name for the kind of picture ("story", "slide") — the
 *                 picker reopens in the folder she last used for it
 */
/* SOURCE-NEUTRAL LABEL, her note 2026-09-23: "another image option would be
   canva so we may want the button to say something else anyway". The button
   says what she is doing; the panel says where the pictures come from — so a
   second source (Canva, uploads) is a tab in here, not a relabelled button. */
/* `children`: the button is that thing instead of the usual "Choose image"
   pill — a picture box she presses to pick (Newsletter, 2026-11-04). */
export function MediaPickButton({ onPick, label = "Choose image", remember = null, children = null, title = undefined }) {
  const { session } = useSession();
  const crm = useCrmConnected();
  const { ref, anchor, open, toggle, close } = useAnchoredPanel();
  const [state, setState] = useState({ loading: false });
  const [q, setQ] = useState("");
  /* FOLDERS (her report, 2026-09-28: "I can't see folders right now"). Where
     she is, as a breadcrumb from the top; Upload and New folder both act HERE. */
  const [path, setPath] = useState(() => readPath(remember));
  const here = path[path.length - 1];
  const [naming, setNaming] = useState(null);   // null, or the new folder's name as she types it
  const [folderBusy, setFolderBusy] = useState(false);
  /* Which tab — remembered per browser, so a Canva person lands on Canva.
     Without a CRM there is one tab, Brill, whatever the browser remembers. */
  const [picked, setSourceState] = useState(() => { try { return window.localStorage.getItem("brill.media.source") || "brill"; } catch { return "brill"; } });
  const sources = crm ? [["brill", "Brill"], ["ghl", "GoHighLevel library"], ["canva", "Canva"]] : [["brill", "Brill"]];
  const source = sources.some(([id]) => id === picked) ? picked : "brill";
  const setSource = (s) => {
    setSourceState(s);
    try { window.localStorage.setItem("brill.media.source", s); } catch { /* private window */ }
    if (s === "ghl" && !state.ok && !state.loading) load();
  };
  const callMedia = async (body) => {
    const token = await session?.getToken();
    const res = await fetch("/api/ghl", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ job: "media", ...body }),
    });
    return res.json().catch(() => ({}));
  };
  /* UPLOAD, 2026-09-23 — her pick for the next GHL step. A picture she has
     on her computer or phone goes INTO her library and is chosen in the same
     move: uploading something only to then hunt for it in the grid is two
     steps where she meant one. */
  const fileRef = useRef(null);
  const [upload, setUpload] = useState({ busy: false, error: "" });
  const sendFile = async (file) => {
    if (!file) return;
    setUpload({ busy: true, error: "" });
    const ready = await prepareImage(file);
    if (!ready.ok) { setUpload({ busy: false, error: ready.error }); return; }
    try {
      const token = await session?.getToken();
      const res = await fetch("/api/ghl", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ job: "media", action: "upload", name: ready.name, contentType: ready.contentType, data: ready.data, parentId: here.id || "" }),
      });
      const r = await res.json().catch(() => ({}));
      if (!r.ok) { setUpload({ busy: false, error: r.error || "The upload didn't go through. Nothing was added." }); return; }
      setState((st) => ({ ...st, pictures: [r.picture, ...(st.pictures || [])] }));
      setUpload({ busy: false, error: "" });
      onPick(r.picture.url);
      close();
    } catch (err) {
      setUpload({ busy: false, error: `Could not reach your CRM — ${err.message}` });
    }
  };

  const load = async (to = path) => {
    setState({ loading: true });
    setQ("");
    try {
      const r = await callMedia({ parentId: to[to.length - 1].id || "" });
      setState({ loading: false, ...r });
    } catch (err) {
      setState({ loading: false, ok: false, error: `Could not reach your CRM — ${err.message}` });
    }
  };
  const go = (to) => { setPath(to); writePath(remember, to); setNaming(null); load(to); };
  /* A NEW FOLDER, made where she is and opened. One she already has at this
     level is opened instead of made twice (the server checks). */
  const makeFolder = async () => {
    const name = String(naming || "").trim();
    if (!name || folderBusy) return;
    setFolderBusy(true);
    try {
      const r = await callMedia({ action: "folder", name, parentId: here.id || "" });
      if (!r.ok) { setUpload({ busy: false, error: r.error || "GHL didn't make the folder." }); return; }
      go([...path, { id: r.folder.id, name: r.folder.name }]);
    } catch (err) {
      setUpload({ busy: false, error: `Could not reach your CRM — ${err.message}` });
    } finally {
      setFolderBusy(false);
    }
  };

  const shown = (state.pictures || []).filter((p) => !q.trim() || p.name.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <span style={{ position: "relative", display: children ? "block" : "inline-block", width: children ? "100%" : undefined }}>
      <button type="button" ref={ref} title={title} aria-label={children ? label : undefined}
        onClick={() => { if (!open && source === "ghl") load(); toggle(); }}
        style={children
          ? { display: "block", width: "100%", padding: 0, border: "none", background: "none", cursor: "pointer", fontFamily: "inherit" }
          : { ...btnGhost, display: "inline-flex", alignItems: "center", gap: 7, padding: "7px 12px", fontSize: 12 }}>
        {children || <><ImageIcon size={13} /> {label}</>}
      </button>

      {open && (
        <div role="dialog" aria-label="Choose a picture"
          style={{
            ...floatingPanel(anchor, { width: 460, wants: 460 }),
            background: B.surface, border: `1px solid ${B.border2}`, borderRadius: 12,
            boxShadow: "var(--shadow-menu)", padding: 12,
          }}>
          {/* WHERE THE PICTURES COME FROM — Brill for everyone; her CRM's
              library and her Canva (C1, 2026-10-01) for admin. One source is
              not a choice, so a client sees no tabs at all. */}
          {sources.length > 1 && <div role="tablist" aria-label="Where from" style={{ display: "flex", gap: 4, marginBottom: 10, borderBottom: `1px solid ${B.border}` }}>
            {sources.map(([id, name]) => (
              <button key={id} type="button" role="tab" aria-selected={source === id} onClick={() => setSource(id)}
                style={{ background: "none", border: "none", borderBottom: `2px solid ${source === id ? B.teal : "transparent"}`, marginBottom: -1,
                  padding: "5px 9px", fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", color: source === id ? B.teal : B.dim }}>
                {name}
              </button>
            ))}
          </div>}
          {source === "brill" ? <BrillPane onPick={onPick} onClose={close} />
            : source === "canva" ? <CanvaPane onPick={onPick} onClose={close} /> : <>
          <div style={{ fontSize: 11, color: B.faint, letterSpacing: "0.04em", textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>
            Your GoHighLevel media library
          </div>
          {/* WHERE SHE IS — each step back up is pressable. */}
          <nav aria-label="Folder" style={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", fontSize: 12.5, marginBottom: 8 }}>
            {path.map((f, i) => (
              <span key={f.id || "top"} style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                {i > 0 && <ChevronRight size={12} color={B.faint} />}
                {i === path.length - 1
                  ? <b style={{ color: B.text, fontWeight: 700 }}>{f.name}</b>
                  : <button type="button" onClick={() => go(path.slice(0, i + 1))}
                      style={{ background: "none", border: "none", padding: "2px 3px", cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, color: B.teal }}>{f.name}</button>}
              </span>
            ))}
          </nav>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name"
              style={{ ...input, padding: "7px 11px", fontSize: 12.5 }} />
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml" hidden
              onChange={(e) => { sendFile(e.target.files?.[0]); e.target.value = ""; }} />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={upload.busy}
              style={{ ...btnGhost, padding: "6px 10px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap", color: B.teal, borderColor: B.tealLine }}>
              <UploadIcon size={12} /> {upload.busy ? "Uploading…" : "Upload"}
            </button>
            <button type="button" onClick={() => setNaming((v) => (v === null ? "" : null))} title="New folder here" aria-label="New folder here"
              style={{ ...btnGhost, padding: "6px 9px", fontSize: 12, display: "inline-flex", alignItems: "center" }}>
              <FolderPlus size={13} />
            </button>
            <button type="button" onClick={close} style={{ ...btnGhost, padding: "6px 10px", fontSize: 12 }}>Close</button>
          </div>
          {naming !== null && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
              <input value={naming} onChange={(e) => setNaming(e.target.value)} autoFocus aria-label="New folder name"
                placeholder={`New folder in ${here.name}`}
                onKeyDown={(e) => { if (e.key === "Enter") makeFolder(); if (e.key === "Escape") setNaming(null); }}
                style={{ ...input, padding: "7px 11px", fontSize: 12.5 }} />
              <button type="button" onClick={makeFolder} disabled={!naming.trim() || folderBusy}
                style={{ ...btnGhost, padding: "6px 10px", fontSize: 12, whiteSpace: "nowrap", color: B.teal, borderColor: B.tealLine, opacity: naming.trim() ? 1 : 0.5 }}>
                {folderBusy ? "Making…" : "Make folder"}
              </button>
            </div>
          )}
          {upload.error && (
            <div style={{ fontSize: 12.5, color: B.amber, lineHeight: 1.6, marginBottom: 10 }}>{upload.error}</div>
          )}

          {state.loading && <div style={{ fontSize: 12.5, color: B.dim, padding: "10px 2px" }}>Looking…</div>}

          {state.ok === false && (
            <div style={{ fontSize: 12.5, color: B.amber, lineHeight: 1.6 }}>{state.error}</div>
          )}

          {state.ok && (state.folders || []).length > 0 && !q.trim() && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
              {state.folders.map((f) => (
                <button key={f.id} type="button" onClick={() => go([...path, { id: f.id, name: f.name }])}
                  style={{ ...btnGhost, padding: "6px 11px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Folder size={13} color={B.teal} /> {f.name}
                </button>
              ))}
            </div>
          )}

          {state.ok && shown.length === 0 && !(state.folders || []).length && (
            /* "Nothing here" and "nothing here that is a picture" are different
               problems, and only one of them is fixed by uploading a logo. */
            <div style={{ fontSize: 12.5, color: B.dim, lineHeight: 1.6 }}>
              {state.seen
                ? `Nothing here is an image — ${state.seen} file${state.seen === 1 ? "" : "s"} in the library, none of them pictures.`
                : here.id ? "Nothing in this folder yet. Upload puts a picture here." : "Your CRM's media library is empty."}
            </div>
          )}

          {shown.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(92px, 1fr))", gap: 8 }}>
              {shown.map((p) => (
                <button key={p.id} type="button" title={p.name}
                  onClick={() => { onPick(p.url); close(); }}
                  style={{
                    border: `1px solid ${B.border}`, borderRadius: 8, padding: 0, background: B.surface2,
                    cursor: "pointer", overflow: "hidden", display: "block",
                  }}>
                  {/* The picture itself, because a filename is not how anyone
                      recognises their own logo. */}
                  <img src={p.url} alt="" style={{ display: "block", width: "100%", height: 66, objectFit: "contain", background: "var(--bg-quiet)" }} />
                  <span style={{ display: "block", fontSize: 10.5, color: B.dim, padding: "4px 5px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.name}
                  </span>
                </button>
              ))}
            </div>
          )}
          </>}
        </div>
      )}
    </span>
  );
}

/* HER PICTURES IN BRILL — the tab every client has. Read when the panel opens
   (it mounts with it), never before: a screen that merely OFFERS a picture
   reads nothing. Upload puts the picture in her folder and chooses it in the
   same move, like the GHL tab. No folders and no delete yet. */
function BrillPane({ onPick, onClose }) {
  const { user } = useUser();
  /* A ref, because `useSupabase()` hands back a new function every render
     and her folder only changes with her. */
  const supaRef = useRef(null);
  supaRef.current = useSupabase();
  const supa = () => supaRef.current();
  const userId = user?.id;
  const [state, setState] = useState({ loading: true });
  const [q, setQ] = useState("");
  const [upload, setUpload] = useState({ busy: false, error: "" });
  const fileRef = useRef(null);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await listPictures(await supaRef.current(), userId);
        if (live) setState({ loading: false, ...r });
      } catch (err) {
        if (live) setState({ loading: false, ok: false, error: `Couldn't open your pictures — ${err.message}` });
      }
    })();
    return () => { live = false; };
  }, [userId]);

  const sendFile = async (file) => {
    if (!file) return;
    setUpload({ busy: true, error: "" });
    const ready = await prepareBlob(file);
    if (!ready.ok) { setUpload({ busy: false, error: ready.error }); return; }
    try {
      const r = await savePicture(await supa(), userId, ready.blob, ready.name);
      if (!r.ok) { setUpload({ busy: false, error: r.error }); return; }
      setState((st) => ({ ...st, pictures: [r.picture, ...(st.pictures || [])] }));
      setUpload({ busy: false, error: "" });
      onPick(r.picture.url);
      onClose();
    } catch (err) {
      setUpload({ busy: false, error: `The upload didn't go through — ${err.message}. Nothing was added.` });
    }
  };

  const shown = (state.pictures || []).filter((p) => !q.trim() || p.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <>
      <div style={{ fontSize: 11, color: B.faint, letterSpacing: "0.04em", textTransform: "uppercase", fontWeight: 700, marginBottom: 6 }}>
        Your pictures in Brill
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name"
          style={{ ...input, padding: "7px 11px", fontSize: 12.5 }} />
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" hidden
          onChange={(e) => { sendFile(e.target.files?.[0]); e.target.value = ""; }} />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={upload.busy || !userId}
          style={{ ...btnGhost, padding: "6px 10px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap", color: B.teal, borderColor: B.tealLine }}>
          <UploadIcon size={12} /> {upload.busy ? "Uploading…" : "Upload"}
        </button>
        <button type="button" onClick={onClose} style={{ ...btnGhost, padding: "6px 10px", fontSize: 12 }}>Close</button>
      </div>
      {/* Said before she uploads, not after: the link is public by design. */}
      <div style={{ fontSize: 11.5, color: B.dim, lineHeight: 1.5, marginBottom: 10 }}>
        For your website and posts. Anyone with a picture's link can see it, so keep private documents out of here.
      </div>
      {upload.error && <div style={{ fontSize: 12.5, color: B.amber, lineHeight: 1.6, marginBottom: 10 }}>{upload.error}</div>}
      {state.loading && <div style={{ fontSize: 12.5, color: B.dim, padding: "10px 2px" }}>Looking…</div>}
      {state.ok === false && <div style={{ fontSize: 12.5, color: B.amber, lineHeight: 1.6 }}>{state.error}</div>}
      {state.ok && (state.pictures || []).length === 0 && (
        <div style={{ fontSize: 12.5, color: B.dim, lineHeight: 1.6 }}>No pictures yet. Upload puts one here and uses it straight away.</div>
      )}
      {shown.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(92px, 1fr))", gap: 8 }}>
          {shown.map((p) => (
            <button key={p.id} type="button" title={p.name} onClick={() => { onPick(p.url); onClose(); }}
              style={{ border: `1px solid ${B.border}`, borderRadius: 8, padding: 0, background: B.surface2, cursor: "pointer", overflow: "hidden", display: "block" }}>
              <img src={p.url} alt="" style={{ display: "block", width: "100%", height: 66, objectFit: "contain", background: "var(--bg-quiet)" }} />
              <span style={{ display: "block", fontSize: 10.5, color: B.dim, padding: "4px 5px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}
